import React from "react";

/**
 * Small presentational helpers shared by the renewal-concierge steps.
 * Kept local to the flow — these encode the wireframes' layout, not
 * app-wide patterns, so they don't belong in `src/components` yet.
 */

export const Card: React.FC<{
  children: React.ReactNode;
  className?: string;
}> = ({ children, className = "" }) => (
  <div className={`rounded-xl bg-white p-5 shadow-sm ${className}`}>
    {children}
  </div>
);

export const StepHeading: React.FC<{ title: string; subtitle?: string }> = ({
  title,
  subtitle,
}) => (
  <div className="mb-5">
    <h2 className="text-xl font-bold text-primary">{title}</h2>
    {subtitle && <p className="mt-1 text-sm text-gray-600">{subtitle}</p>}
  </div>
);

export const PrimaryButton: React.FC<{
  children: React.ReactNode;
  onClick?: () => void;
  disabled?: boolean;
  type?: "button" | "submit";
  variant?: "primary" | "success";
  className?: string;
}> = ({
  children,
  onClick,
  disabled = false,
  type = "button",
  variant = "primary",
  className = "",
}) => (
  <button
    type={type}
    onClick={onClick}
    disabled={disabled}
    className={`rounded-lg px-6 py-2.5 text-sm font-medium text-white transition-colors ${
      disabled
        ? "cursor-not-allowed bg-gray-300"
        : variant === "success"
          ? "cursor-pointer bg-success hover:opacity-90"
          : "cursor-pointer bg-primary hover:bg-primary-light"
    } ${className}`}
  >
    {children}
  </button>
);

export const SecondaryButton: React.FC<{
  children: React.ReactNode;
  onClick?: () => void;
  className?: string;
}> = ({ children, onClick, className = "" }) => (
  <button
    type="button"
    onClick={onClick}
    className={`cursor-pointer rounded-lg border border-gray-300 bg-white px-6 py-2.5 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 ${className}`}
  >
    {children}
  </button>
);

/** Back / Continue footer used at the bottom of every step. */
export const StepNav: React.FC<{
  onBack?: () => void;
  onNext?: () => void;
  nextLabel?: string;
  backLabel?: string;
  nextDisabled?: boolean;
  nextVariant?: "primary" | "success";
}> = ({
  onBack,
  onNext,
  nextLabel = "Continue →",
  backLabel = "← Back",
  nextDisabled = false,
  nextVariant = "primary",
}) => (
  // Buttons size to their own content. `justify-between` keeps Back on the
  // left and the CTA on the right; `ml-auto` pushes the CTA right on steps
  // that have no Back button.
  <div className="mt-6 flex items-center justify-between gap-3">
    {onBack && (
      <SecondaryButton onClick={onBack}>{backLabel}</SecondaryButton>
    )}
    {onNext && (
      <PrimaryButton
        onClick={onNext}
        disabled={nextDisabled}
        variant={nextVariant}
        className="ml-auto"
      >
        {nextLabel}
      </PrimaryButton>
    )}
  </div>
);

/** Yes / No pair used by the Notes and Condition steps. */
export const YesNo: React.FC<{
  value: boolean | null;
  onChange: (v: boolean) => void;
}> = ({ value, onChange }) => (
  <div className="grid grid-cols-2 gap-3">
    {[
      { label: "Yes", val: true },
      { label: "No", val: false },
    ].map((opt) => (
      <button
        key={opt.label}
        type="button"
        onClick={() => onChange(opt.val)}
        className={`cursor-pointer rounded-lg border-2 px-4 py-3 text-sm font-medium transition-colors ${
          value === opt.val
            ? "border-primary bg-primary/5 text-primary"
            : "border-gray-200 bg-white text-gray-700 hover:border-gray-300"
        }`}
      >
        {opt.label}
      </button>
    ))}
  </div>
);

export const Notice: React.FC<{
  tone: "warning" | "info" | "danger";
  title?: string;
  children: React.ReactNode;
}> = ({ tone, title, children }) => {
  const cls = {
    warning: "bg-amber-50 text-amber-900",
    info: "bg-sky-50 text-sky-900",
    danger: "bg-red-50 text-red-900",
  }[tone];
  return (
    <div className={`rounded-lg px-4 py-3 text-sm ${cls}`}>
      {title && <p className="mb-1 font-semibold">{title}</p>}
      <div>{children}</div>
    </div>
  );
};

/** Character-counted textarea. */
export const CountedTextarea: React.FC<{
  value: string;
  onChange: (v: string) => void;
  maxLength: number;
  placeholder?: string;
  rows?: number;
}> = ({ value, onChange, maxLength, placeholder, rows = 4 }) => (
  <div>
    <textarea
      value={value}
      onChange={(e) => onChange(e.target.value.slice(0, maxLength))}
      placeholder={placeholder}
      rows={rows}
      className="w-full rounded-lg border border-gray-300 px-4 py-3 text-sm transition-all focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
    />
    <div className="mt-1 text-right text-xs text-muted">
      {value.length}/{maxLength}
    </div>
  </div>
);

/** Label + value row for the Review screens. */
export const ReviewRow: React.FC<{
  label: string;
  value: React.ReactNode;
}> = ({ label, value }) => (
  <div className="flex items-baseline justify-between gap-6 border-b border-gray-100 px-5 py-2.5 last:border-b-0">
    <span className="shrink-0 text-sm text-gray-600">{label}</span>
    <span className="text-right text-sm font-semibold leading-relaxed text-primary">
      {value || "—"}
    </span>
  </div>
);
